import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface EventRow {
  readonly threatId: string;
  readonly threatCode: string;
  readonly threatType: string;
  readonly detectionSource: string;
  readonly title: string;
  readonly severity: string;
  readonly status: string;
  readonly alertGenerated: boolean;
}

const TYPES = [
  '', 'BRUTE_FORCE', 'CREDENTIAL_STUFFING', 'UNAUTHORIZED_ACCESS', 'PRIVILEGE_ESCALATION',
  'API_ABUSE', 'DATA_EXFILTRATION', 'INSIDER_THREAT', 'SUSPICIOUS_ACTIVITY', 'POLICY_VIOLATION',
];
const SEVERITIES = ['', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'];
const STATUSES = ['', 'DETECTED', 'INVESTIGATING', 'CONFIRMED', 'MITIGATED', 'RESOLVED', 'FALSE_POSITIVE', 'ESCALATED'];

/**
 * GR L30.01 — Security Events (`/security/events`). Live feed of the
 * `threat_detection` signal store: detected threats with their type, source,
 * severity and triage status, and whether an alert was raised. Filterable by
 * threat type, severity and status.
 */
export function GrSecurityEventsPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<EventRow[]>([]);
  const [threatType, setThreatType] = useState('');
  const [severity, setSeverity] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (threatType) params.set('threatType', threatType);
    if (severity) params.set('severity', severity);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: EventRow[] }>(`/v1/security/threats${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, threatType, severity, status]);

  return (
    <main aria-label="security-events">
      <h1>{tr('gr30.events.title')}</h1>
      <div className="filters">
        <label>
          {tr('gr30.events.labelType')}
          <select aria-label="filter-type" value={threatType} onChange={(e) => setThreatType(e.target.value)}>
            {TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('gr30.events.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('gr30.events.labelSeverity')}
          <select aria-label="filter-severity" value={severity} onChange={(e) => setSeverity(e.target.value)}>
            {SEVERITIES.map((s) => <option key={s || 'all'} value={s}>{s || tr('gr30.events.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('gr30.events.labelStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || tr('gr30.events.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('gr30.events.loading')}</p> : (
        <>
          <p aria-label="event-count">{tr('gr30.events.count', { count: String(rows.length) })}</p>
          {rows.length === 0 ? <p aria-label="empty">{tr('gr30.events.empty')}</p> : (
            <table aria-label="event-table">
              <thead><tr><th>{tr('gr30.events.colCode')}</th><th>{tr('gr30.events.colType')}</th><th>{tr('gr30.events.colSource')}</th><th>{tr('gr30.events.colSeverity')}</th><th>{tr('gr30.events.colStatus')}</th><th>{tr('gr30.events.colAlert')}</th></tr></thead>
              <tbody>
                {rows.map((e) => (
                  <tr key={e.threatId}>
                    <td>{e.threatCode}</td><td>{e.threatType}</td><td>{e.detectionSource}</td>
                    <td>{e.severity}</td><td>{e.status}</td><td>{e.alertGenerated ? tr('gr30.events.yes') : tr('gr30.events.no')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </main>
  );
}
